import CloseIcon from '@assets/icons/close.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Icons } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { Dropdown } from '@usepepper/ui/atoms/dropdown';
import { Pressable, TextInput } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';

interface AddCompetitorProps {
  competitorName: string | null;
  disabled: boolean;
  onAddCompetitor: (name: string) => void;
  onRemoveCompetitor: () => void;
}

/**
 * Trigger + dropdown panel that names a competitor for the current quote.
 * When unset: shows a "+ Add Competitor" button that opens a small dropdown
 * with a text input + confirm. When set: collapses to a chip with the
 * competitor name and a remove × that clears the competitor (and, via the
 * `SET_COMPETITOR_NAME` reducer action, the per-line competitor data).
 *
 * Disabled until an account is selected.
 */
export const AddCompetitor: React.FC<AddCompetitorProps> = ({
  competitorName,
  disabled,
  onAddCompetitor,
  onRemoveCompetitor,
}) => {
  const [styles, theme] = useStyles();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<TextInput>(null);

  const hasCompetitor = competitorName !== null;

  // Defer focus by one frame so the dropdown has finished its open
  // transition; preventScroll keeps focus from scrolling the page sideways.
  // Same pattern we use in custom-item-panel.
  useEffect(() => {
    if (!isPanelOpen) {
      return;
    }
    const id = requestAnimationFrame(() => {
      const node = inputRef.current as
        | (TextInput & { focus: (opts?: { preventScroll?: boolean }) => void })
        | null;
      node?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [isPanelOpen]);

  const handleOpenFromButton = () => {
    setInputValue('');
    setIsPanelOpen(true);
  };

  const handleOpenFromPill = () => {
    setInputValue(competitorName ?? '');
    setIsPanelOpen(true);
  };

  const handleClose = () => {
    setIsPanelOpen(false);
    setInputValue('');
  };

  const handleConfirm = () => {
    const trimmed = inputValue.trim();
    if (trimmed && trimmed !== competitorName) {
      onAddCompetitor(trimmed);
    }
    setIsPanelOpen(false);
    setInputValue('');
  };

  const handleRemove = () => {
    onRemoveCompetitor();
    setIsPanelOpen(false);
    setInputValue('');
  };

  const confirmLabel = hasCompetitor
    ? i18n.t('sales_portal.price_quotes.add_competitor.save_button')
    : i18n.t('sales_portal.price_quotes.add_competitor.add_button');

  const trigger = hasCompetitor ? (
    <Pressable
      accessibilityLabel={competitorName ?? ''}
      onPress={handleOpenFromPill}
      style={styles.activeChip}
    >
      <Typography color={theme.sk.color.text.primary} variant="paragraph-md">
        {competitorName}
      </Typography>
      <Pressable
        accessibilityLabel={i18n.t(
          'sales_portal.price_quotes.remove_competitor',
        )}
        hitSlop={8}
        onPress={(e) => {
          // Don't let the close press bubble up and reopen the dropdown.
          e.stopPropagation();
          handleRemove();
        }}
      >
        <CloseIcon
          fill={theme.sk.color.icon.secondary}
          height={14}
          width={14}
        />
      </Pressable>
    </Pressable>
  ) : (
    <Button
      accessibilityLabel={i18n.t('sales_portal.price_quotes.add_competitor')}
      disabled={disabled}
      icon={<Icons.Plus size={16} />}
      onPress={handleOpenFromButton}
      size="$sm"
      variant="secondary"
    >
      {i18n.t('sales_portal.price_quotes.add_competitor')}
    </Button>
  );

  return (
    <Dropdown
      containerStyle={styles.panel}
      isVisible={isPanelOpen}
      onClose={handleClose}
      position="bottom-start"
      trigger={trigger}
    >
      <Typography
        color={theme.sk.color.text.secondary}
        variant="paragraph-sm-600"
      >
        {i18n.t('sales_portal.price_quotes.add_competitor.label')}
      </Typography>
      <TextInput
        accessibilityLabel={i18n.t(
          'sales_portal.price_quotes.add_competitor.label',
        )}
        onChangeText={setInputValue}
        onSubmitEditing={handleConfirm}
        placeholder={i18n.t(
          'sales_portal.price_quotes.add_competitor.placeholder',
        )}
        placeholderTextColor={theme.sk.color.text.tertiary}
        ref={inputRef}
        selectTextOnFocus
        style={[styles.input, { color: theme.sk.color.text.primary }]}
        value={inputValue}
      />
      <Button
        accessibilityLabel={confirmLabel}
        disabled={!inputValue.trim() || inputValue.trim() === competitorName}
        onPress={handleConfirm}
        size="$sm"
        variant="primary"
        width="100%"
      >
        {confirmLabel}
      </Button>
    </Dropdown>
  );
};

const useStyles = createStyles((theme) => ({
  activeChip: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.tertiary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    height: theme.sk.spacing.spaceXlarge,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  input: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    fontFamily: theme.sk.typography.paragraph.md.fontFamily,
    fontSize: theme.sk.typography.paragraph.md.fontSize,
    minWidth: 220,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  panel: {
    gap: theme.sk.spacing.spaceSmall,
    padding: theme.sk.spacing.spaceMedium,
  },
}));
